import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import AddHabitButton from 'components/setting-generator-revamp/tabs-revamp/AddHabitButton';
import DraggableContentRevamp from 'components/setting-generator-revamp/tabs-body-steps-revamp/DraggableContentRevamp';
import { ACTIVITY_TYPES } from 'constants/general';
import { ActivityType } from 'interfaces/common.interface';
import { useCallback, useContext, useMemo } from 'react';
import { t } from 'services/i18n.service';
import { useAppDispatch } from 'store/hooks';
import { changeActivityOrder } from 'store/reducer/setting/slice';
import { v4 as uuid } from 'uuid';
import { CustomRoutineContext } from '.';

const CustomRoutineActivities = () => {
  const dispatch = useAppDispatch();
  const customRoutine = useContext(CustomRoutineContext);
  const [activities, sequence_id] = useMemo(
    () => [
      customRoutine?.standalone_activities ?? [],
      customRoutine?.standalone_activities?.[0]?.activity_sequence_id ?? uuid()
    ],
    [customRoutine?.standalone_activities]
  );

  const activityIds = useMemo(
    () => activities.map((activity) => String(activity.id)),
    [activities]
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = activityIds.indexOf(String(active.id));
      const newIndex = activityIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const reordered = arrayMove(Array.from(activities), oldIndex, newIndex);
      dispatch(
        changeActivityOrder({
          type: ACTIVITY_TYPES.CUSTOM_ROUTINES,
          data: reordered,
          customRoutinePosition: customRoutine?.position
        })
      );
    },
    [activities, activityIds, customRoutine?.position, dispatch]
  );

  return (
    <>
      {activities?.length ? (
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <div
            className='w-full h-fit flex flex-col items-center ~gap-10/12'
            data-testid='custom-routine-activity-list'>
            <SortableContext
              items={activityIds}
              strategy={verticalListSortingStrategy}>
              {activities.map((activity: ActivityType, index: number) => (
                <DraggableContentRevamp
                  key={activity.id}
                  id={String(activity.id)}
                  position={index}
                  type={ACTIVITY_TYPES.CUSTOM_ROUTINES}
                  sequence_id={sequence_id}
                  customRoutinePosition={customRoutine?.position}
                />
              ))}
            </SortableContext>
          </div>
        </DndContext>
      ) : null}
      <AddHabitButton
        routine={
          !activities.length
            ? customRoutine?.name || t('custom_routine')
            : undefined
        }
        title={
          !activities.length
            ? customRoutine?.name || t('custom_routine')
            : undefined
        }
        customRoutinePosition={customRoutine?.position}
        data-testid='add-habit-btn-custom-routine'
      />
    </>
  );
};

export default CustomRoutineActivities;
